import { useCallback } from "react";

import { Raster } from "@nens/lizard/api-v4";

import { useWmsGetColorRangeQueryDirectly } from "@/queries/query-wms-color-range";
import { RasterFileDescriptor } from "@/types/descriptor-types";

import { usePublicationStore } from "../../../store/store";
import { RasterLayer } from "../../../types/publication";

/**
 * Manage style override state and sync with store
 */
export function useConfigureRasterMinMax({
  layer,
  rasterDescriptor,
  raster,
}: {
  layer: RasterLayer;
  rasterDescriptor: RasterFileDescriptor | undefined;
  raster: Raster | undefined;
}) {
  const fetchColorRange = useWmsGetColorRangeQueryDirectly();
  const replaceLayer = usePublicationStore((state) => state.replaceLayer);
  const updateMinMax = useCallback(
    (minMax: [number, number] | null) => {
      replaceLayer({
        ...layer,
        properties: { ...layer.properties, minMax: minMax ?? layer.properties.initialMinMax },
      });
    },
    [layer, replaceLayer]
  );

  const setOpacity = useCallback(
    (opacity: number) => {
      replaceLayer({ ...layer, opacity });
    },
    [layer, replaceLayer]
  );

  const setColorBounds = useCallback(
    async (bounds: [number, number, number, number]) => {
      if (!rasterDescriptor || !raster) return;
      const { isSuccess, data } = await fetchColorRange(
        rasterDescriptor,
        raster.wms_info.layer,
        bounds
      );
      if (!isSuccess) {
        return;
      }
      updateMinMax(data);
    },
    [fetchColorRange, raster, rasterDescriptor, updateMinMax]
  );

  return {
    setDefaultColor: () => updateMinMax(layer.properties.initialMinMax),
    setColorRange: updateMinMax,
    setColorBounds,
    setOpacity,
  };
}
